import { db } from './firebase';
import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';

export interface Feature {
  id: string;
  title: string;
  description: string;
  icon: string;
  order: number;
  active: boolean;
}

class FeatureService {
  private readonly COLLECTION = 'features';
  
  // Get all features, sorted by order
  async getAllFeatures(): Promise<Feature[]> {
    try {
      const q = query(collection(db, this.COLLECTION), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Feature[];
    } catch (error) {
      console.error('Error fetching features:', error);
      throw error;
    }
  }
  
  // Get only active features for the public site
  async getActiveFeatures(): Promise<Feature[]> {
    try {
      const features = await this.getAllFeatures();
      return features.filter(feature => feature.active);
    } catch (error) {
      console.error('Error fetching active features:', error);
      throw error;
    }
  }
  
  // Get a single feature by ID
  async getFeature(id: string): Promise<Feature | null> {
    try {
      const docRef = doc(db, this.COLLECTION, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }
      
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Feature;
    } catch (error) {
      console.error(`Error fetching feature with ID ${id}:`, error);
      throw error;
    }
  }
  
  // Add a new feature
  async addFeature(feature: Omit<Feature, 'id'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, this.COLLECTION), feature);
      return docRef.id;
    } catch (error) {
      console.error('Error adding feature:', error);
      throw error;
    }
  }
  
  // Update an existing feature
  async updateFeature(id: string, updates: Partial<Omit<Feature, 'id'>>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, id);
      await updateDoc(docRef, updates);
    } catch (error) {
      console.error(`Error updating feature with ID ${id}:`, error);
      throw error;
    }
  }
  
  // Delete a feature
  async deleteFeature(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting feature with ID ${id}:`, error);
      throw error;
    }
  }
  
  // Reorder features
  async updateFeatureOrder(orderedIds: string[]): Promise<void> {
    try {
      // Update each feature with its new order
      const batch = Promise.all(
        orderedIds.map((id, index) => 
          this.updateFeature(id, { order: index })
        )
      );
      
      await batch;
    } catch (error) {
      console.error('Error updating feature order:', error);
      throw error;
    }
  }
}

export const featureService = new FeatureService(); 